<script setup>
import { computed, onMounted, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import useAPIForm from "@/composables/useAPIForm";
import { useCardOptionsMenuStore } from "@/stores/cardOptionsMenu";
import { useToastStore } from "@/stores/toast.js";
import { useModalogStore } from "@/stores/modalog";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";
import * as Sentry from "@sentry/vue";

const cardOptionsStore = useCardOptionsMenuStore();
const modalogStore = useModalogStore();
const toastStore = useToastStore();

const props = defineProps({
    isOwner: {
        type: Boolean,
        default: false,
    },
    item: {
        type: Object,
        default: null,
    },
});

const form = useAPIForm({
    name: "",
    privacy: "public",
    type: "wish",
});

// Experts open on the Expert tab but can still pick a regular wish list —
// same rule as the Create List modalog (GH-997, BH-67).
const isGiftHero = computed(() => !!user.value?.is_gift_hero);

function seedForm() {
    form.reset();

    if (isGiftHero.value) {
        form.type = "expert";
    }
}

onMounted(seedForm);

// The host keeps this instance mounted between opens (stable-root
// pattern), so the form re-seeds on every open (GH-955).
watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            seedForm();
        }
    },
);

const actionVerb = computed(() => {
    return props.isOwner ? "moved" : "copied";
});

const buttonText = computed(() => {
    return props.isOwner ? "Create & Move" : "Create & Copy";
});

const busyButtonText = computed(() => {
    return props.isOwner ? "Creating & Moving" : "Creating & Copying";
});

const isItemDetail = computed(() => {
    return usePage().component === "Shared/ItemDetail";
});

const listTypeMoniker = computed(() => {
    return form.type.charAt(0).toUpperCase() + form.type.substring(1);
});

const user = computed(() => {
    return usePage().props.auth.user?.data;
});

const titleText = computed(() => {
    return props.isOwner
        ? `Move to New ${listTypeMoniker.value} List`
        : `Copy to New ${listTypeMoniker.value} List`;
});

function moveOrCopyGiftToNewWishList() {
    form.post(route("giftlist.store"), {
        preserveScroll: true,
        onSuccess: (newGiftlist) => {
            axios({
                method: props.isOwner ? "put" : "post",
                url: route(props.isOwner ? "item.move" : "item.copy", {
                    item: props.item,
                }),
                data: {
                    item_uuid: props.item.uuid,
                    list_uuid: newGiftlist.uuid,
                },
            })
                .catch((error) => {
                    console.error(error);
                    toastStore.error(
                        `There was an error ${props.isOwner ? "moving" : "copying"} the item.`,
                    );
                    Sentry.captureException(error);
                })
                .finally(() => {
                    modalogStore.closeModalog();
                    cardOptionsStore.closeAll();
                    form.reset();
                    toastStore.success(
                        `"${props.item.name}" was ${actionVerb.value.toLowerCase()} to "${newGiftlist.name}".`,
                    );
                    props.item.is_processing = true;
                    props.item.is_processing_text = `${props.item.name} ${actionVerb.value} to ${newGiftlist.name}`;

                    setTimeout(() => {
                        if (isItemDetail.value) {
                            router.visit(
                                route("item.detail", { item: props.item }),
                            );
                        } else {
                            router.reload({
                                preserveScroll: true,
                                only: ["items"],
                            });
                        }
                    }, 3000);
                });
        },
        onError: (error) => {
            console.error(error);
            toastStore.error(
                `There was an error ${props.isOwner ? "moving" : "copying"} the item.`,
            );
            Sentry.captureException(error);
        },
    });
}

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy placeholder:text-[#3c3938]/50 focus:border-brand-navy focus:outline-none";
const labelClasses = "mb-1.5 block text-[15px] font-bold text-[#3c3938]";
</script>

<template>
    <BrandConfirmModalog
        :title="titleText"
        :confirm-label="buttonText"
        :busy-label="busyButtonText"
        :busy="form.processing"
        :confirm-disabled="!form.name"
        @confirm="moveOrCopyGiftToNewWishList"
    >
        <ul
            v-if="form.hasErrors"
            class="mb-4 text-[14px] font-semibold text-brand-red"
        >
            <li v-for="(error, key) in form.errors" :key="key">
                {{ error }}
            </li>
        </ul>

        <!--List Name-->
        <div>
            <label :class="labelClasses" for="wishlist-name"
                >{{ listTypeMoniker }} List Name</label
            >
            <input
                id="wishlist-name"
                :class="inputClasses"
                type="text"
                name="list-name"
                v-model="form.name"
            />
        </div>

        <!--List Type: same segmented control as the Create List modalog-->
        <div
            class="mt-4 flex h-14 items-stretch gap-1.5 rounded-[8px] border border-solid border-[#e6dfdd] p-1.5"
            role="tablist"
        >
            <button
                type="button"
                role="tab"
                class="flex-1 rounded-[5px] text-[15px] font-bold transition"
                :class="
                    form.type !== 'expert'
                        ? 'bg-brand-navy text-white'
                        : 'text-brand-navy hover:bg-brand-navy/5'
                "
                :aria-selected="form.type !== 'expert'"
                @click="form.type = 'wish'"
            >
                New Wish List
            </button>
            <button
                type="button"
                role="tab"
                class="flex flex-1 items-center justify-center gap-2 rounded-[5px] text-[15px] font-bold transition"
                :class="
                    form.type === 'expert'
                        ? 'bg-brand-navy text-white'
                        : 'text-brand-navy hover:bg-brand-navy/5'
                "
                :aria-selected="form.type === 'expert'"
                @click="form.type = 'expert'"
            >
                <svg
                    viewBox="0 0 19 23"
                    class="h-[22px] w-[18px]"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M9.08823 0L0 4.01299V10.0325C0 15.6005 3.87765 20.8073 9.08823 22.0714C14.2988 20.8073 18.1765 15.6005 18.1765 10.0325V4.01299L9.08823 0Z"
                        fill="url(#ghShieldMoveNewListTab)"
                    />
                    <path
                        d="M13.9272 6.49583L10.2154 7.20229C10.0691 7.22851 9.96183 7.36463 9.96183 7.52409V10.6053C9.92691 10.6146 9.89128 10.624 9.8539 10.6322C9.63167 10.6825 9.36889 10.713 9.08811 10.713C8.83309 10.713 8.59252 10.6879 8.38406 10.6455C8.32409 10.6333 8.26766 10.6193 8.21369 10.6049V7.52445C8.21369 7.36499 8.10646 7.22887 7.96008 7.20265L4.24898 6.49619C4.06344 6.46279 3.89484 6.61615 3.89484 6.81799V14.5466C3.89484 14.7061 4.00207 14.8422 4.14845 14.8684L7.85955 15.5749C8.04508 15.6083 8.21369 15.4549 8.21369 15.2531V12.5508C8.4927 12.5961 8.78547 12.6194 9.08811 12.6198C9.3904 12.6198 9.68282 12.5957 9.96148 12.5508V15.2535C9.96148 15.4553 10.1304 15.6087 10.316 15.5753L14.0278 14.8688C14.1742 14.8426 14.2814 14.7065 14.2814 14.547V6.81835C14.2814 6.61651 14.1124 6.46315 13.9269 6.49655L13.9272 6.49583Z"
                        fill="#fff"
                    />
                    <defs>
                        <linearGradient
                            id="ghShieldMoveNewListTab"
                            x1="6.4916"
                            y1="1.83929"
                            x2="17.1247"
                            y2="25.6074"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stop-color="#FFB545" />
                            <stop offset="1" stop-color="#DB7712" />
                        </linearGradient>
                    </defs>
                </svg>
                New Expert List
            </button>
        </div>

        <!--List Privacy-->
        <div class="mt-4">
            <p :class="labelClasses">
                Who Can See this {{ listTypeMoniker }} List?
            </p>
            <div class="space-y-2">
                <label
                    class="flex cursor-pointer items-start gap-3 rounded-[5px] border border-solid p-3.5 transition"
                    :class="
                        form.privacy === 'public'
                            ? 'border-brand-navy bg-brand-cream/40'
                            : 'border-[#e6dfdd] hover:bg-brand-cream/30'
                    "
                >
                    <input
                        type="radio"
                        name="list-visibility"
                        value="public"
                        v-model="form.privacy"
                        class="mt-0.5 size-4 shrink-0 accent-[#f21439]"
                    />
                    <span class="block">
                        <span
                            class="block text-[15px] font-bold text-brand-navy"
                            >Public</span
                        >
                        <span class="block text-[13px] text-[#3c3938]"
                            >Anyone online</span
                        >
                    </span>
                </label>
                <label
                    class="flex cursor-pointer items-start gap-3 rounded-[5px] border border-solid p-3.5 transition"
                    :class="
                        form.privacy === 'private'
                            ? 'border-brand-navy bg-brand-cream/40'
                            : 'border-[#e6dfdd] hover:bg-brand-cream/30'
                    "
                >
                    <input
                        type="radio"
                        name="list-visibility"
                        value="private"
                        v-model="form.privacy"
                        class="mt-0.5 size-4 shrink-0 accent-[#f21439]"
                    />
                    <span class="block">
                        <span
                            class="block text-[15px] font-bold text-brand-navy"
                            >Private Link</span
                        >
                        <span class="block text-[13px] text-[#3c3938]"
                            >Only those with a link to this
                            {{ listTypeMoniker.toLowerCase() }} list</span
                        >
                    </span>
                </label>
            </div>
        </div>
    </BrandConfirmModalog>
</template>
